import { authRequest } from './client';
import {
  type ListWithdrawalsParams,
  type ListWithdrawalsResponse,
  type RejectWithdrawalInput,
  type WithdrawalDetailResponse,
} from './types';

// ─── backoffice-api withdrawals client ─────────────────────────────────────
//
// Thin fetch wrappers around backoffice-api's `/v1/withdrawals` endpoints
// (Feature 030 Task 6, extended feature-036 Task 8). `listWithdrawals`/
// `getWithdrawal` are FINANCE_VIEWER OR CONFIG_MANAGER; `approveWithdrawal`/
// `rejectWithdrawal` are CONFIG_MANAGER-only, resolving a LIMIT hold —
// mirrors gas-station.ts's read/write role split. `releaseWithdrawal`/
// `officerRejectWithdrawal` are AML_OFFICER-only, resolving a COMPLIANCE
// hold — a separate path from approve/reject, never interchangeable (the
// backend 409s a wrong-kind-hold action regardless of the caller's role).
// `getWithdrawal`/`approveWithdrawal`/`rejectWithdrawal`/`releaseWithdrawal`/
// `officerRejectWithdrawal` all return the same `WithdrawalDetailResponse`
// shape (list-item fields + the full `screenings` history).

export function listWithdrawals(params: ListWithdrawalsParams = {}): Promise<ListWithdrawalsResponse> {
  const query = new URLSearchParams();
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  if (params.status !== undefined) query.set('status', params.status);
  if (params.merchantId !== undefined) query.set('merchantId', params.merchantId);
  const qs = query.toString();
  return authRequest<ListWithdrawalsResponse>(`/withdrawals${qs ? `?${qs}` : ''}`);
}

export function getWithdrawal(id: string): Promise<WithdrawalDetailResponse> {
  return authRequest<WithdrawalDetailResponse>(`/withdrawals/${id}`);
}

// The controller's approve/release routes take no `@Body()` at all —
// `authRequest`'s underlying `fetch` call tolerates `{ method: 'POST' }`
// with no `body` key present (fetchRaw only sets `Content-Type:
// application/json` when `init.body !== undefined`), so these omit `body`
// entirely rather than sending an empty-object placeholder.
export function approveWithdrawal(id: string): Promise<WithdrawalDetailResponse> {
  return authRequest<WithdrawalDetailResponse>(`/withdrawals/${id}/approve`, { method: 'POST' });
}

export function rejectWithdrawal(
  id: string,
  input: RejectWithdrawalInput,
): Promise<WithdrawalDetailResponse> {
  return authRequest<WithdrawalDetailResponse>(`/withdrawals/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** `POST /v1/withdrawals/:id/aml/release` (feature-036, AML_OFFICER) — releases a compliance hold, returning the withdrawal to APPROVED. */
export function releaseWithdrawal(id: string): Promise<WithdrawalDetailResponse> {
  return authRequest<WithdrawalDetailResponse>(`/withdrawals/${id}/aml/release`, { method: 'POST' });
}

/** `POST /v1/withdrawals/:id/aml/reject` (feature-036, AML_OFFICER) — rejects a compliance-held withdrawal; `input.reason` is shown to the merchant. */
export function officerRejectWithdrawal(
  id: string,
  input: RejectWithdrawalInput,
): Promise<WithdrawalDetailResponse> {
  return authRequest<WithdrawalDetailResponse>(`/withdrawals/${id}/aml/reject`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
